import { isEmpty } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import IdleTimer from 'react-idle-timer'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Modal from 'rsuite/Modal'
import * as auth from 'src/app/modules/auth'
import { FiLogOut } from 'react-icons/fi'
import { RootState } from 'src/setup'
import { access } from 'fs'
import AuthService from '../constants/authService'

const IDLE_TIMEOUT = 30 * 60 * 1000
const LAST_ACTIVITY_KEY = 'lastActivityAt'
const ACTIVITY_WRITE_THROTTLE = 5000
const LOGOUT_NOTICE_DELAY = 3000 // how long the "logged out" modal shows before redirecting

const IdleTimerContainer = () => {
  const idleTimerRef: any = useRef(null)
  const sessionTimeoutRef: any = useRef(null)
  const redirectTimeoutRef: any = useRef(null)
  const lastWriteRef = useRef(0)
  const dispatch = useDispatch()
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [loggedOutModal, setLoggedOutModal] = useState(false)
  const [isLoggedIn, setLoggedIn] = useState(false)
  const { accessToken, guestToken }: any = useSelector<RootState>(({ auth }) => auth, shallowEqual)
  useEffect(() => {
    if (!isEmpty(accessToken)) {
      setLoggedIn(true)
    } else {
      setLoggedIn(false)
    }
  }, [accessToken])

  // Every tab writes its latest activity to one shared localStorage key, so idle
  // time is measured across ALL open tabs: a stale background tab must not log
  // out (and kill the token for) a tab the user is working in.
  const markActive = () => {
    const now = Date.now()
    if (now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE) return
    lastWriteRef.current = now
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    } catch (e) {}
  }

  const getLastActivity = () => {
    try {
      return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0
    } catch (e) {
      return 0
    }
  }

  // This tab has been idle for IDLE_TIMEOUT; only log out if every other tab
  // has been too. Otherwise re-check when the shared idle window would expire.
  const onIdle = () => {
    clearTimeout(sessionTimeoutRef.current)
    const idleFor = Date.now() - getLastActivity()
    if (idleFor < IDLE_TIMEOUT) {
      sessionTimeoutRef.current = setTimeout(onIdle, IDLE_TIMEOUT - idleFor)
      return
    }
    setModalOpen(true)
  }

  useEffect(() => {
    if (isLoggedIn) {
      setLoggedOutModal(false)
      lastWriteRef.current = 0
      markActive()
    }
  }, [isLoggedIn])

  useEffect(
    () => () => {
      clearTimeout(sessionTimeoutRef.current)
      clearTimeout(redirectTimeoutRef.current)
    },
    []
  )

  // Show the "logged out" notice first, while still authenticated: clearing auth
  // swaps Routes to AuthPage, which redirects to SSO on the same render, so a
  // modal opened together with the logout would never be seen.
  const logOut = () => {
    setModalOpen(false)
    setLoggedOutModal(true)
    clearTimeout(sessionTimeoutRef.current)
    clearTimeout(redirectTimeoutRef.current)
    redirectTimeoutRef.current = setTimeout(finishLogOut, LOGOUT_NOTICE_DELAY)
  }

  const finishLogOut = () => {
    clearTimeout(redirectTimeoutRef.current)
    setLoggedIn(false)
    dispatch(auth.actions.logout())
  }

  const userActive = (e: any) => {
    setIsTimedOut(false)
    clearTimeout(sessionTimeoutRef.current)
    markActive()
  }

  const staySignedIn = () => {
    setModalOpen(false)
    setLoggedOutModal(false)
    clearTimeout(sessionTimeoutRef.current)
  }

  // Log out as an effect, never during render. Dispatching + setState from the
  // render phase re-enters React's reconciler while it is already rendering,
  // which desyncs the fiber tree from the DOM and surfaces later as
  // "Failed to execute 'removeChild' on 'Node'" when the logged-in subtree unmounts.
  useEffect(() => {
    if (isLoggedIn && modalOpen) {
      logOut()
    }
  }, [isLoggedIn, modalOpen])

  return (
    <div>
      <IdleTimer
        ref={idleTimerRef}
        onActive={userActive}
        timeout={IDLE_TIMEOUT}
        onIdle={onIdle}
        onAction={markActive}
      ></IdleTimer>
      {/* {isLoggedIn && modalOpen && (
        <>
          <Modal open={modalOpen}>
            <h2>You've been idle</h2>
            <p>You will be logged out soon</p>
            <div className='d-flex justify-content-end px-9 '>
              <button onClick={staySignedIn} className='btn btn-primary btn-sm ms-3'>
                Stay Signed In
              </button>
              <button onClick={logOut} className='btn btn-secondary btn-sm ms-3'>
                Log Out
              </button>
            </div>
          </Modal>
        </>
      )} */}

      {loggedOutModal && (
        <div>
          <Modal
            open={loggedOutModal}
            onClose={() => {
              if (isLoggedIn) return
              setLoggedOutModal(false)
              setModalOpen(false)
            }}
            backdrop={isLoggedIn ? 'static' : true}
            className='w-25'
            enforceFocus={false}
          >
            <Modal.Body className='text-center'>
              <FiLogOut className='mb-3 text-danger' style={{ fontSize: '50px' }} />
              <h2 className='d-flex justify-content-center'>You have been logged out for being idle</h2>
              <p className='text-muted mb-0'>
                You were inactive for {IDLE_TIMEOUT / 60000} minute
                {IDLE_TIMEOUT === 60000 ? '' : 's'}. Redirecting to login…
              </p>
            </Modal.Body>
            {isLoggedIn && (
              <Modal.Footer>
                <button onClick={finishLogOut} className='btn btn-primary btn-sm'>
                  OK
                </button>
              </Modal.Footer>
            )}
          </Modal>
          <IdleTimer ref={idleTimerRef} timeout={IDLE_TIMEOUT}></IdleTimer>
        </div>
      )}
    </div>
  )
}

export default IdleTimerContainer
